import React, { useEffect, useState } from "react";
import AddSiteModal from "./AddSiteModal";
import { API_URL } from "../../../config";

// Lets a distributor view/change/create their active site. Extracted out of
// QuickActionsSidebar so it can live wherever the page wants it (e.g. inside
// MiniCart's header) instead of being tied to the shared sidebar.
const SiteSwitcher = () => {
  const userType = localStorage.getItem("user_type");
  const distributorId = localStorage.getItem("user_id");
  const isDistributor = userType === "6";

  const [sites, setSites] = useState([]);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [siteQuery, setSiteQuery] = useState("");
  const [addSiteModal, setAddSiteModal] = useState(false);
  const [activeSiteId, setActiveSiteId] = useState(localStorage.getItem("site_id") || "");
  const [activeSiteName, setActiveSiteName] = useState(localStorage.getItem("site_name") || "");
  const [siteIdCopied, setSiteIdCopied] = useState(false);

  const filteredSites = sites.filter((s) =>
    s.name.toLowerCase().includes(siteQuery.toLowerCase())
  );

  useEffect(() => {
    if (!isDistributor || !distributorId) return;
    fetch(`${API_URL}/distributor/site/${distributorId}`, {
      headers: { "x-access-token": localStorage.getItem("accessToken") },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setSites(data.data);
          const currentId = localStorage.getItem("site_id");
          const current = data.data.find((s) => s.site_id === currentId);
          if (current) {
            localStorage.setItem("site_name", current.name);
            setActiveSiteName(current.name);
          } else if (data.data.length > 0) {
            selectSite(data.data[0]);
          }
        }
      })
      .catch(() => {});
    }, [isDistributor, distributorId]);
  

  const selectSite = (site, { reload = false } = {}) => {
    const changed = localStorage.getItem("site_id") !== site.site_id;
    localStorage.setItem("site_id", site.site_id);
    localStorage.setItem("site_name", site.name);
    setActiveSiteId(site.site_id);
    setActiveSiteName(site.name);
    setSiteQuery("");
    setSiteDropdownOpen(false);
    // Other pages fetch their data once on mount, keyed off site_id -- there's
    // no shared state to notify them a different site is now active, so force
    // a refetch by reloading whenever the user explicitly changes the site.
    if (reload && changed) {
      window.location.reload();
    }
  };

  const handleSiteCreated = (site) => {
    setSites((prev) => [...prev, site]);
    selectSite(site, { reload: true });
  };

  const toggleAddSiteModal = () => {
    setSiteDropdownOpen(false);
    setAddSiteModal(!addSiteModal);
  };

  // The site_id is what API integrations pass (see API Integration page).
  const copySiteId = async () => {
    if (!activeSiteId) return;
    try {
      await navigator.clipboard.writeText(activeSiteId);
      setSiteIdCopied(true);
      setTimeout(() => setSiteIdCopied(false), 1500);
    } catch (err) {
      console.error("Error copying site id:", err);
    }
  };

  const openSiteDropdown = () => {
    setSiteQuery("");
    setSiteDropdownOpen(true);
  };

  if (!isDistributor) return null;

  return (
    <>
      <div className="site-switcher">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "2px" }}>
          <span className="site-switcher-label" style={{ marginBottom: 0 }}>Select Site</span>
          {activeSiteId && (
            <button
              type="button"
              className="site-switcher-label"
              // Keep a mouse click from focusing it (no focus outline)
              onMouseDown={(e) => e.preventDefault()}
              onClick={copySiteId}
              title={siteIdCopied ? "Copied!" : `Copy site ID: ${activeSiteId}`}
              style={{
                marginBottom: 0,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textTransform: "none",
              }}
            >
              <i className={siteIdCopied ? "fas fa-check" : "fas fa-copy"}></i>{" "}
              {siteIdCopied ? "Copied" : "Copy ID"}
            </button>
          )}
        </div>
        <input
          type="text"
          className="site-switcher-input"
          placeholder="Select Site"
          value={siteDropdownOpen ? siteQuery : activeSiteName}
          onFocus={openSiteDropdown}
          onChange={(e) => setSiteQuery(e.target.value)}
          onBlur={() => setSiteDropdownOpen(false)}
        />

        {siteDropdownOpen && (
          <div className="site-switcher-panel">
            <div className="site-switcher-list">
              {filteredSites.length === 0 && (
                <div className="site-switcher-empty">
                  {sites.length === 0 ? "No sites yet" : "No matching sites"}
                </div>
              )}
              {filteredSites.map((site) => (
                <div
                  key={site.id}
                  className={`site-switcher-item ${site.site_id === activeSiteId ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectSite(site, { reload: true });
                  }}
                >
                  <span className="site-switcher-item-name">{site.name}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="site-switcher-add-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleAddSiteModal();
              }}
            >
              + Add Site
            </button>
          </div>
        )}
      </div>

      <AddSiteModal isOpen={addSiteModal} toggle={toggleAddSiteModal} onCreated={handleSiteCreated} />
    </>
  );
};

export default SiteSwitcher;
